// Per-day, per-room, per-slot manual marks for the board's EMPTY staff slots.
//
// A LOCAL, per-day display overlay ONLY (localStorage). It controls what an empty doctor/CRNA slot shows,
// so the board runner can drive the prompt by hand:
//   'need' -> force the red "NEED MD/CRNA" brick (flag a slot as required, even before allocation)
//   'none' -> muted "NONE" brick (mark the slot intentionally unstaffed — doctor-only room, etc.)
// A slot with NO mark falls back to the automatic behavior (auto-NEED once the board is staffed). Marks
// never touch a persisted allocation, and a mark is cleared automatically once a person is placed in the slot.
//
// Key = `${operatingRoomId}::${slotKind}`, slotKind ∈ 'doctor' | 'crna' | 'student'.

export type SlotMark = 'need' | 'none';

const keyFor = (date: string) => `oc-slot-marks-${date}`;
const mk = (operatingRoomId: string, slotKind: string) => `${operatingRoomId}::${slotKind}`;

export function loadSlotMarks(date: string): Record<string, SlotMark> {
  try {
    const raw = localStorage.getItem(keyFor(date));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// Clear EVERY slot mark for a date. Used when a new simulation replaces the day, so old NEED/NONE marks (keyed
// by the stable operatingRoomId) don't linger on the fresh board.
export function clearSlotMarks(date: string): void {
  try { localStorage.removeItem(keyFor(date)); } catch { /* ignore */ }
}

export function getSlotMark(marks: Record<string, SlotMark>, operatingRoomId: string, slotKind: string): SlotMark | undefined {
  return marks[mk(operatingRoomId, slotKind)];
}

// Set (or, with mark === null, clear) a slot's mark. Returns a NEW object so React state updates re-render.
export function setSlotMark(date: string, operatingRoomId: string, slotKind: string, mark: SlotMark | null): Record<string, SlotMark> {
  const marks = loadSlotMarks(date);
  const key = mk(operatingRoomId, slotKind);
  if (mark === null) delete marks[key];
  else marks[key] = mark;
  try { localStorage.setItem(keyFor(date), JSON.stringify(marks)); } catch { /* best-effort */ }
  return { ...marks };
}
